import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import useAdminDialog from '../components/AdminDialog';
import { useAdminLanguage } from '../AdminLanguageContext';

const roleBadge = { admin: 'badge-purple', user: 'badge-blue', banned: 'badge-red' };
const verificationBadge = { verified: 'badge-green', unverified: 'badge-gray' };

function yuan(cents) {
  return `¥${((Number(cents) || 0) / 100).toFixed(2)}`;
}

export default function Users() {
  const { copy } = useAdminLanguage();
  const usersCopy = copy.users;
  const roleLabel = {
    admin: usersCopy.roles.admin,
    user: usersCopy.roles.user,
    banned: usersCopy.roles.banned,
  };
  const verificationLabel = {
    verified: usersCopy.verification.verified,
    unverified: usersCopy.verification.unverified,
  };
  const adminRoleLabel = {
    super_admin: usersCopy.adminRoles.super_admin,
    operations: usersCopy.adminRoles.operations,
    finance: usersCopy.adminRoles.finance,
    content: usersCopy.adminRoles.content,
    readonly: usersCopy.adminRoles.readonly,
  };
  const adminRoleOptions = [
    { value: 'super_admin', label: usersCopy.adminRoles.super_admin },
    { value: 'operations', label: usersCopy.adminRoles.operations },
    { value: 'finance', label: usersCopy.adminRoles.finance },
    { value: 'content', label: usersCopy.adminRoles.content },
    { value: 'readonly', label: usersCopy.adminRoles.readonly },
  ];
  const marketplaceRoleLabel = {
    admin: usersCopy.marketplaceRoles.admin,
    buyer: usersCopy.marketplaceRoles.buyer,
    seller: usersCopy.marketplaceRoles.seller,
    both: usersCopy.marketplaceRoles.both,
  };
  const [data, setData] = useState({ users: [], total: 0 });
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentAdminRole, setCurrentAdminRole] = useState('readonly');
  const { confirmAction, dialog } = useAdminDialog();

  const fetch = useCallback(() => {
    setLoading(true);
    api.get('/admin/users', { params: { keyword, page, limit: 20 } })
      .then(res => {
        setData(res.data);
        setCurrentAdminRole(res.data.currentAdminRole || 'readonly');
      })
      .finally(() => setLoading(false));
  }, [keyword, page]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleBan = async (id, role) => {
    const action = role === 'banned' ? usersCopy.actions.unban : usersCopy.actions.ban;
    if (!await confirmAction({ title: usersCopy.confirm.ban.title(action), message: usersCopy.confirm.ban.message(action), variant: role === 'banned' ? 'primary' : 'danger' })) return;
    await api.put(`/admin/users/${id}/ban`);
    fetch();
  };

  const handleSetAdmin = async (id, role) => {
    const newRole = role === 'admin' ? 'user' : 'admin';
    const newRoleLabel = newRole === 'admin' ? usersCopy.roles.admin : usersCopy.roles.user;
    if (!await confirmAction({ title: usersCopy.confirm.role.title, message: usersCopy.confirm.role.message(newRoleLabel) })) return;
    await api.put(`/admin/users/${id}/role`, { role: newRole });
    fetch();
  };

  const handleAdminRole = async (id, adminRole) => {
    if (!await confirmAction({ title: usersCopy.confirm.adminRole.title, message: usersCopy.confirm.adminRole.message })) return;
    await api.put(`/admin/users/${id}/admin-role`, { adminRole });
    fetch();
  };

  const handleVerification = async (id, status) => {
    const nextStatus = status === 'verified' ? 'unverified' : 'verified';
    const action = nextStatus === 'verified' ? usersCopy.actions.setVerified : usersCopy.actions.unverify;
    if (!await confirmAction({ title: usersCopy.confirm.verification.title, message: usersCopy.confirm.verification.message(action) })) return;
    await api.put(`/admin/users/${id}/verification`, { status: nextStatus });
    fetch();
  };

  const handleDelete = async (id) => {
    if (!await confirmAction({ title: usersCopy.confirm.delete.title, message: usersCopy.confirm.delete.message, variant: 'danger' })) return;
    await api.delete(`/admin/users/${id}`);
    fetch();
  };

  return (
    <div>
      {dialog}
      <div className="page-title">{usersCopy.title}</div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="search-input">
            <input
              placeholder={usersCopy.searchPlaceholder}
              value={keyword}
              onChange={e => { setKeyword(e.target.value); setPage(1); }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>{usersCopy.total(data.total)}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{usersCopy.columns.id}</th><th>{usersCopy.columns.username}</th><th>{usersCopy.columns.email}</th><th>{usersCopy.columns.region}</th><th>{usersCopy.columns.marketplaceRole}</th><th>{usersCopy.columns.role}</th><th>{usersCopy.columns.adminRole}</th><th>{usersCopy.columns.verification}</th>
                <th>{usersCopy.columns.orders}</th><th>{usersCopy.columns.spending}</th><th>{usersCopy.columns.points}</th><th>{usersCopy.columns.tasks}</th><th>{usersCopy.columns.posts}</th><th>{usersCopy.columns.createdAt}</th><th>{usersCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={15} style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>{usersCopy.loading}</td></tr>
              ) : data.users.map(u => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td style={{ fontWeight: 600 }}>{u.username}</td>
                  <td style={{ color: '#6b7280' }}>{u.email}</td>
                  <td style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {[u.country, u.city, u.district].filter(Boolean).join(' / ') || usersCopy.emptyValue}
                  </td>
                  <td><span className="badge badge-gray">{marketplaceRoleLabel[u.marketplace_role] || usersCopy.marketplaceRoles.buyer}</span></td>
                  <td><span className={`badge ${roleBadge[u.role] || 'badge-gray'}`}>{roleLabel[u.role] || u.role}</span></td>
                  <td>
                    {u.role === 'admin' && currentAdminRole === 'super_admin' ? (
                      <select
                        value={u.admin_role || 'super_admin'}
                        onChange={e => handleAdminRole(u.id, e.target.value)}
                      >
                        {adminRoleOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : u.role === 'admin' ? (
                      adminRoleLabel[u.admin_role || 'super_admin']
                    ) : usersCopy.emptyValue}
                  </td>
                  <td>
                    <span className={`badge ${verificationBadge[u.verification_status] || 'badge-gray'}`}>
                      {verificationLabel[u.verification_status] || usersCopy.verification.unverified}
                    </span>
                  </td>
                  <td>{u.order_count || 0}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{yuan(u.spending_amount)}</td>
                  <td>{u.points || 0}</td>
                  <td>{u.task_count}</td>
                  <td>{u.post_count}</td>
                  <td style={{ color: '#6b7280' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {u.role !== 'admin' && (
                        <button className={`btn btn-sm ${u.role === 'banned' ? 'btn-outline' : 'btn-warning'}`}
                          onClick={() => handleBan(u.id, u.role)}>
                          {u.role === 'banned' ? usersCopy.actions.unban : usersCopy.actions.ban}
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline"
                        onClick={() => handleSetAdmin(u.id, u.role)}>
                        {u.role === 'admin' ? usersCopy.actions.demote : usersCopy.actions.setAdmin}
                      </button>
                      <button className="btn btn-sm btn-outline"
                        onClick={() => handleVerification(u.id, u.verification_status)}>
                        {u.verification_status === 'verified' ? usersCopy.actions.unverify : usersCopy.actions.setVerified}
                      </button>
                      <button className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(u.id)}>
                        {usersCopy.actions.delete}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <button className="btn btn-sm btn-outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>{usersCopy.previous}</button>
          <span>{usersCopy.page(page)}</span>
          <button className="btn btn-sm btn-outline" disabled={page * 20 >= data.total} onClick={() => setPage(p => p + 1)}>{usersCopy.next}</button>
        </div>
      </div>
    </div>
  );
}
